import React, { useState, useEffect } from "react";
import { Project, CreateProjectData } from "../../store/types";
import { isNotEmpty, isAlphanumeric } from "../../hooks/validation";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import CustomInputText from "../CustomInput/CustomInputText";
import { StyledIconLine } from "../StyledIcon/StyledIcon";
import { DangerText } from "../../styles/Typography";
import {
  IconId,
  IconIdFilled,
  IconCase,
  IconCaseFilled,
} from "../../assets/icons/Icon";
import { Column, SmallColumn } from "./Dialogs.styles";

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  dialogType: string;
  project?: Project;
  handleAddProject?: (company: CreateProjectData) => Promise<boolean>;
  handleEditProject?: (company: Project) => Promise<boolean>;
  handleDeleteProject?: (company: Project) => Promise<boolean>;
  refetchData?: () => {};
}

const initialFormData: CreateProjectData = {
  id: "",
  name: "",
};

const initialErrors: Record<keyof CreateProjectData, boolean> = {
  id: false,
  name: false,
};

const ProjectDialogs: React.FC<DialogProps> = ({
  visible,
  onHide,
  dialogType,
  project,
  handleAddProject,
  handleEditProject,
  handleDeleteProject,
  refetchData,
}) => {
  const [formData, setFormData] = useState<CreateProjectData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateProjectData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: keyof CreateProjectData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateProjectData, boolean> = {
      id: !isAlphanumeric(formData.id),
      name: !isNotEmpty(formData.name),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleIdBlur = (field: keyof CreateProjectData) => {
    const value = formData[field];
    const isValid = isAlphanumeric(value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleBlur = (field: keyof CreateProjectData) => {
    const value = formData[field];
    const isValid = isNotEmpty(value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (dialogType !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (dialogType === "create" && handleAddProject) {
        success = await handleAddProject!(formData);
      } else if (dialogType === "edit" && handleEditProject) {
        success = await handleEditProject!({ ...formData, id: project!.id });
      } else if (dialogType === "delete" && handleDeleteProject) {
        success = await handleDeleteProject!(project!);
      }
      if (success) {
        onHide();
        refetchData!();
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setValidForm(true);
      if (dialogType === "edit" && project) {
        const newData = {
          id: project.id || "",
          name: project.name || "",
        };
        setFormData(newData);
        const newErrors: Record<keyof CreateProjectData, boolean> = {
          id: !isAlphanumeric(newData.id),
          name: !isNotEmpty(newData.name),
        };
        setErrors(newErrors);
      } else {
        setFormData(initialFormData);
        setErrors(initialErrors);
      }
    }
  }, [visible, dialogType, project]);

  useEffect(() => {
    if (!visible) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setValidForm(true);
      setSubmitting(false);
    }
  }, [visible]);

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новый проект";
      case "edit":
        return "Редактировать проект";
      case "delete":
        return "Удалить проект";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const footerContent = (
    <div>
      <Button label="Отмена" onClick={onHide} outlined />
      <Button
        label={
          dialogType === "delete"
            ? "Удалить"
            : dialogType === "edit"
            ? "Сохранить"
            : "Создать"
        }
        onClick={handleSubmit}
        loading={submitting}
      />
    </div>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      <CustomInputText
        value={formData.id}
        setValue={(val) => handleFieldChange("id", val)}
        onBlur={() => handleIdBlur("id")}
        placeholder="id"
        icon={<IconId size={20} />}
        invalid={errors.id}
      />
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Название"
        icon={<IconCase size={20} />}
        invalid={errors.name}
      />
    </Column>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данный проект?</DangerText>
      <SmallColumn>
        <StyledIconLine
          icon={<IconIdFilled size={20} />}
          text={project?.id ?? "Нет данных"}
        />
        <StyledIconLine
          icon={<IconCaseFilled size={20} />}
          text={project?.name ?? "Нет данных"}
        />
      </SmallColumn>
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      default:
        return <></>;
    }
  };

  return (
    <Dialog
      header={getHeader(dialogType)}
      visible={visible}
      style={{ width: "500px" }}
      onHide={onHide}
      footer={footerContent}
    >
      {getContent(dialogType)}
    </Dialog>
  );
};

export default ProjectDialogs;
